import React, {
  useCallback,
  useEffect,
  useState,
  useRef,
  useImperativeHandle,
  forwardRef,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslate } from 'react-translate';
import PropTypes from 'prop-types';

import LanguageSwitch from 'components/Languages/LanguageSwitchHeader';
// #ux-redesign: UserMenu removido del navbar — la identidad del usuario
// (avatar + nombre) vive en la user-card del sidebar; logout también desde
// el sidebar. Evita doble cluster.
import Notifications from 'components/Notifications/Notifications';
import HelpMenu from 'components/Navbars/Menu/HelpMenu';

// #ux-redesign-start: theme toggle + responsive + dropdown "Mas" en mobile (Paso 3 shell)
import ThemeToggle from 'components/Navbars/Menu/ThemeToggle';
import useResponsive from 'hooks/useResponsive';
import { useTheme } from 'contexts/ThemeContext';
import { actionSetLang } from 'store/actions/usersActions';
import {
  Dropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
} from 'reactstrap';
// #ux-redesign-end

import {
  Collapse,
  Navbar,
  NavbarToggler,
  NavbarBrand,
  Nav,
  Container,
} from 'reactstrap';

import Icon from 'components/Icons/Icon';
import useAuthorizationValidator from 'plugins/TS-lib-utils-public/hooks/useAuthorizationValidator';

const AdminNavbar = forwardRef((props, ref) => {
  const [isOpen, setIsOpen] = useState(false);
  const [color, setColor] = useState('white');
  // #ux-redesign-start: estado del dropdown "Mas" en mobile
  const [moreOpen, setMoreOpen] = useState(false);
  const { isMobile } = useResponsive();
  const { theme, setTheme } = useTheme();
  const tNav = useTranslate('navbar');
  const tSidebar = useTranslate('sidebar');
  const ta = useTranslate('a11y');
  const dispatch = useDispatch();
  const currentLang = useSelector((state) => state.userReducer.language);
  // #ux-redesign-end

  const isMounted = useRef(true);
  const sidebarToggle = useRef(null);

  const toggle = () => {
    setColor('white');
    setIsOpen(!isOpen);
  };

  useImperativeHandle(ref, () => ({
    hideSidebar() {
      openSidebar();
    },
  }));

  const { checkAccess } = useAuthorizationValidator();

  const openSidebar = () => {
    document.documentElement.classList.toggle('nav-open');
    sidebarToggle.current.classList.toggle('toggled');
  };

  const path_route = props.history.location.pathname;
  const updateColor = useCallback(
    (e) => {
      if (isMounted.current) setColor('white');
      if (typeof e === 'undefined') return null;
      const sidebar = sidebarToggle.current;
      if (
        window.innerWidth < 992 &&
        window.location &&
        path_route !== window.location.pathname &&
        document.documentElement.className.indexOf('nav-open') !== -1 &&
        sidebar
      ) {
        document.documentElement.classList.toggle('nav-open');
        sidebar.classList.toggle('toggled');
      }
    },
    [isMounted, path_route]
  );

  useEffect(() => {
    isMounted.current = true;
    window.addEventListener('resize', updateColor);
    return () => {
      isMounted.current = false;
      window.removeEventListener('resize', updateColor);
    };
  }, [isMounted, updateColor]);

  // #ux-redesign: navegar desde el dropdown "Mas"
  const go = (path) => () => {
    setMoreOpen(false);
    if (props.history) props.history.push(path);
  };

  return (
    <Navbar
      color={
        window.location.href.indexOf('full-screen-maps') !== -1 ? 'white' : color
      }
      expand="xs"
      className="navbar-header"
    >
      <Container fluid>
        <div className="navbar-wrapper">
          <div className="navbar-toggle">
            <button
              type="button"
              ref={sidebarToggle}
              className="navbar-toggler"
              onClick={openSidebar}
              aria-label={ta('open-menu')}
            >
              <span className="navbar-toggler-bar bar1" />
              <span className="navbar-toggler-bar bar2" />
              <span className="navbar-toggler-bar bar3" />
            </button>
          </div>
          <NavbarBrand
            href="#"
            onClick={() => window.location.reload()}
            className="terminservice-name"
          >
            <Icon file="logo_telepraxen_patienten.svg" alt="TS" width="120px" />
          </NavbarBrand>
        </div>

        <NavbarToggler onClick={toggle}>
          <span className="navbar-toggler-bar navbar-kebab" />
          <span className="navbar-toggler-bar navbar-kebab" />
          <span className="navbar-toggler-bar navbar-kebab" />
        </NavbarToggler>

        <Collapse isOpen={isOpen} navbar className="justify-content-end">
          <Nav navbar>
            {/* #ux-redesign-start: navbar reorganizado por viewport
                Desktop: todos los items visibles.
                Mobile: solo UserMenu + ⋮Mas. El resto adentro del "Mas" para
                reducir clutter en pantallas chicas (90% del tráfico). */}

            {!isMobile && <LanguageSwitch />}
            {!isMobile && <ThemeToggle />}
            {!isMobile && <HelpMenu activeRoute={path_route} />}

            {!isMobile && (
              <Notifications
                type="messages"
                notifications={[]}
                title={ta('messages')}
                icon="icon-inbox"
              />
            )}
            {!isMobile && (
              <Notifications
                type="laboratory"
                notifications={[]}
                title={ta('laboratory')}
                icon="icon-message-header"
              />
            )}

            {/* #ux-redesign: UserMenu eliminado del navbar.
                El acceso a perfil/logout vive en la user-card + botón logout
                del sidebar. */}

            {/* Mobile: dropdown "Mas" con items SIMPLES (no componentes complejos).
                Cada item es accionable directo: link a vista o cambio de setting. */}
            {isMobile && (
              <Dropdown
                nav
                isOpen={moreOpen}
                toggle={() => setMoreOpen(!moreOpen)}
                className="ux-navbar-more"
              >
                <DropdownToggle nav aria-label={ta('more-options')}>
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <circle cx="12" cy="5" r="1.5" />
                    <circle cx="12" cy="12" r="1.5" />
                    <circle cx="12" cy="19" r="1.5" />
                  </svg>
                </DropdownToggle>
                <DropdownMenu right className="ux-navbar-more__menu">
                  {/* Navegación rápida */}
                  <div className="ux-navbar-more__group-label">
                    {tNav('navigation')}
                  </div>
                  <DropdownItem onClick={go('/admin/inbox')}>
                    <i className="icon-inbox ux-navbar-more__icon" />
                    <span>{tSidebar('inbox')}</span>
                  </DropdownItem>
                  <DropdownItem onClick={go('/admin/lab-studies')}>
                    <i className="icon-microscope ux-navbar-more__icon" />
                    <span>{tSidebar('laboratory')}</span>
                  </DropdownItem>
                  <DropdownItem onClick={go('/admin/doctors')}>
                    <i className="icon-user-active ux-navbar-more__icon" />
                    <span>{tSidebar('doctors')}</span>
                  </DropdownItem>

                  <DropdownItem divider />

                  {/* Theme */}
                  <div className="ux-navbar-more__group-label">
                    {tNav('appearance')}
                  </div>
                  <DropdownItem
                    onClick={() => setTheme('light')}
                    active={theme === 'light'}
                  >
                    <span className="ux-navbar-more__icon">☀</span>
                    <span>{tNav('theme-light')}</span>
                    {theme === 'light' && (
                      <span className="ux-navbar-more__check">✓</span>
                    )}
                  </DropdownItem>
                  <DropdownItem
                    onClick={() => setTheme('dark')}
                    active={theme === 'dark'}
                  >
                    <span className="ux-navbar-more__icon">🌙</span>
                    <span>{tNav('theme-dark')}</span>
                    {theme === 'dark' && (
                      <span className="ux-navbar-more__check">✓</span>
                    )}
                  </DropdownItem>

                  <DropdownItem divider />

                  {/* Language */}
                  <div className="ux-navbar-more__group-label">
                    {tNav('language')}
                  </div>
                  {[
                    { code: 'de', label: 'Deutsch' },
                    { code: 'en', label: 'English' },
                    { code: 'es', label: 'Español' },
                    { code: 'ru', label: 'Русский' },
                  ].map((l) => (
                    <DropdownItem
                      key={l.code}
                      onClick={() => dispatch(actionSetLang(l.code))}
                      active={currentLang === l.code}
                    >
                      <span className="ux-navbar-more__icon ux-navbar-more__lang-abbr">
                        {l.code.toUpperCase()}
                      </span>
                      <span>{l.label}</span>
                      {currentLang === l.code && (
                        <span className="ux-navbar-more__check">✓</span>
                      )}
                    </DropdownItem>
                  ))}

                  <DropdownItem divider />

                  {/* Cuenta */}
                  <DropdownItem onClick={go('/admin/settings')}>
                    <i className="icon-settings1 ux-navbar-more__icon" />
                    <span>{tSidebar('user-settings')}</span>
                  </DropdownItem>
                </DropdownMenu>
              </Dropdown>
            )}
            {/* #ux-redesign-end */}
          </Nav>
        </Collapse>
      </Container>
    </Navbar>
  );
});

AdminNavbar.defaultProps = {
  brandText: 'Default Brand Text',
};

AdminNavbar.propTypes = {
  brandText: PropTypes.string,
};

export default React.memo(AdminNavbar, (prevProps, nextProps) => {
  return prevProps?.location?.pathname === nextProps?.location?.pathname;
});
